import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useInView } from "../hooks";
import { imgUrl } from "../api";
import { useCategories } from "../hooks";

function useVisibleCount() {
  const getCount = useCallback(() => {
    const w = window.innerWidth;
    if (w < 640) return 1;
    if (w < 1024) return 2;
    return 3;
  }, []);
  const [count, setCount] = useState(getCount);
  useEffect(() => {
    const handle = () => setCount(getCount());
    window.addEventListener("resize", handle, { passive: true });
    return () => window.removeEventListener("resize", handle);
  }, [getCount]);
  return count;
}

// Skeleton card
function SkeletonCard() {
  return (
    <div
      className="flex-none rounded-br-[40px] bg-stone-200 animate-pulse"
      style={{ aspectRatio: "4/5" }}
    />
  );
}

export default function FranchiseCategories() {
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const [sectionRef, inView] = useInView(0.1);
  const [activeIndex, setActiveIndex] = useState(0);
  const dragStart = useRef(null);
  const visibleCount = useVisibleCount();
  const { categories, loading } = useCategories();
  const GAP = 16;

  const maxIndex = Math.max(0, categories.length - visibleCount);

  useEffect(() => {
    if (activeIndex > maxIndex) setActiveIndex(maxIndex);
  }, [maxIndex, activeIndex]);

  const goTo = useCallback(
    (idx) => {
      const next = Math.max(0, Math.min(idx, maxIndex));
      setActiveIndex(next);
      if (trackRef.current) {
        const cardW = (trackRef.current.offsetWidth + GAP) / visibleCount;
        trackRef.current.scrollTo({ left: next * cardW, behavior: "smooth" });
      }
    },
    [maxIndex, visibleCount],
  );

  const onPointerDown = (e) => {
    dragStart.current = e.clientX;
  };
  const onPointerUp = (e) => {
    if (dragStart.current === null) return;
    const delta = dragStart.current - e.clientX;
    if (Math.abs(delta) > 40) goTo(activeIndex + (delta > 0 ? 1 : -1));
    dragStart.current = null;
  };

  const handleCategoryClick = (cat) => {
    navigate(`/brands?category=${cat._id}`);
    window.scrollTo({ top: 0 });
  };

  const showNext = activeIndex < maxIndex;
  const showPrev = activeIndex > 0;

  // Fallback images for categories without images
  const fallbackImgs = [
    "/images/french3.jpg",
    "/images/hero_image.jpg",
    "/images/about_us.jpg",
    "/images/french1.jpg",
    "/images/french2.jpg",
    "/images/french4.jpg",
  ];

  return (
    <section
      id="categories"
      ref={sectionRef}
      className="bg-cream py-14 md:py-20 px-4 sm:px-8 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`flex items-start justify-between mb-8 md:mb-10 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <div>
            <span className="inline-block font-body text-[10px] tracking-[0.25em] uppercase text-[#D29D4E] border border-[#D29D4E] px-3 py-1 rounded-full mb-4">
              Franchise Categories
            </span>
            <h2
              className="font-display font-normal text-stone-900 leading-tight"
              style={{ fontSize: "clamp(1.7rem, 4vw, 2.8rem)" }}
            >
              Popular Franchise Categories
            </h2>
          </div>
          <button
            onClick={() => {
              navigate("/brands");
              window.scrollTo({ top: 0 });
            }}
            className="flex items-center gap-2 font-body text-[11px] tracking-[0.1em] uppercase text-stone-700 hover:text-gold transition-colors duration-200 mt-1 flex-shrink-0 ml-6 group"
          >
            <span className="hidden sm:inline">All Categories</span>
            <span className="w-8 h-8 rounded-full bg-gold group-hover:bg-stone-900 flex items-center justify-center text-white text-xs transition-colors duration-300">
              {categories.length || ""}
            </span>
          </button>
        </div>

        {/* Slider */}
        <div
          className={`relative transition-all duration-700 delay-150 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {/* Left arrow */}
          <button
            onClick={() => goTo(activeIndex - 1)}
            aria-label="Previous"
            className={`absolute -left-4 top-1/2 -translate-y-[60%] z-20 w-12 h-12 rounded-full bg-stone-900 hover:bg-gold text-white flex items-center justify-center shadow-2xl transition-all duration-300 ${showPrev ? "opacity-100 scale-100" : "opacity-0 scale-90 pointer-events-none"}`}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          {/* Track */}
          <div
            ref={trackRef}
            className="flex overflow-hidden"
            style={{ gap: `${GAP}px`, cursor: "grab", userSelect: "none" }}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerLeave={() => {
              dragStart.current = null;
            }}
          >
            {loading
              ? [...Array(visibleCount)].map((_, i) => (
                  <div
                    key={i}
                    className="flex-none"
                    style={{
                      width: `calc(${100 / visibleCount}% - ${(GAP * (visibleCount - 1)) / visibleCount}px)`,
                    }}
                  >
                    <SkeletonCard />
                  </div>
                ))
              : categories.map((cat, i) => (
                  <div
                    key={cat._id}
                    className="flex-none"
                    style={{
                      width: `calc(${100 / visibleCount}% - ${(GAP * (visibleCount - 1)) / visibleCount}px)`,
                    }}
                    onClick={() => handleCategoryClick(cat)}
                  >
                    <div
                      className="relative overflow-hidden rounded-br-[40px] group cursor-pointer"
                      style={{ aspectRatio: "4/5" }}
                    >
                      <img
                        src={
                          cat.img
                            ? imgUrl(cat.img)
                            : fallbackImgs[i % fallbackImgs.length]
                        }
                        alt={cat.name}
                        draggable="false"
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                        <p className="font-display text-white font-light text-lg md:text-xl mb-2 leading-snug">
                          {cat.name}
                        </p>
                        {cat.description && (
                          <p className="font-body text-white/60 text-xs md:text-sm leading-relaxed">
                            {cat.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
          </div>

          {/* Right arrow */}
          <button
            onClick={() => goTo(activeIndex + 1)}
            aria-label="Next"
            className={`absolute right-0 top-1/2 -translate-y-[60%] translate-x-1/2 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full bg-stone-900 hover:bg-gold text-white flex items-center justify-center shadow-2xl transition-all duration-300 ${showNext ? "opacity-100 scale-100" : "opacity-0 scale-90 pointer-events-none"}`}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        {/* Dots */}
        {maxIndex > 0 && (
          <div className="flex justify-center gap-2 mt-7">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1 rounded-full transition-all duration-300 ${i === activeIndex ? "w-7 bg-stone-800" : "w-2 bg-stone-300 hover:bg-stone-400"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
